// ============================================================
//  Bragsheet React — theme.js
//  React port of www/theme.js's window.BSTheme: five named themes,
//  localStorage persistence, and applying data-theme to <html> (the
//  :root[data-theme="…"] blocks in styles.css do the recolouring).
//
//  Faithful to the vanilla engine's public shape (get/set/apply/isDark/
//  THEMES/LABELS/DEFAULT). Deliberately OMITTED from the port:
//    - syncStatusBar(): native Capacitor StatusBar only, a no-op in a
//      browser. This SPA is web-only (Phase 0), so it's not needed.
//    - the ds-pending desktop-shell flash veil: desktop-shell infra that
//      doesn't exist in the React app.
//  localStorage-only — no Firestore.
// ============================================================

// SAME key as the vanilla app, so a user's existing theme choice carries over.
const THEME_KEY = 'bragsheet_theme';

const THEMES = ['editorial', 'midnight', 'clay', 'aurora', 'contrast'];

const LABELS = {
  editorial: 'Editorial',
  midnight:  'Midnight',
  clay:      'Clay',
  aurora:    'Aurora',
  contrast:  'Contrast',
};

// Themes that render on a dark background.
const DARK = ['midnight', 'aurora'];

const DEFAULT = 'editorial';

/** Current theme key. Falls back to DEFAULT if missing/invalid or storage throws. */
function get() {
  let saved = null;
  try { saved = localStorage.getItem(THEME_KEY); } catch { /* storage unavailable */ }
  return THEMES.includes(saved) ? saved : DEFAULT;
}

/** Apply the current theme to <html>. Safe to call repeatedly. */
function apply() {
  document.documentElement.setAttribute('data-theme', get());
}

/** Persist + apply a theme, then notify listeners via a 'bsthemechange' event. */
function set(key) {
  if (!THEMES.includes(key)) return;
  try { localStorage.setItem(THEME_KEY, key); } catch { /* storage unavailable */ }
  apply();
  window.dispatchEvent(new CustomEvent('bsthemechange', { detail: { theme: key } }));
  console.log(`[Theme] Set to: ${key}`);
}

/** Is the given theme (or the current one) a dark theme? */
function isDark(key) {
  return DARK.includes(key || get());
}

const BSTheme = { THEMES, LABELS, DARK, DEFAULT, get, set, apply, isDark };

// Apply the saved theme immediately on import — this module is imported first
// in main.jsx, so data-theme is on <html> before React renders (no flash).
apply();

// Expose globally for parity with the vanilla app and for console testing
// (there is no theme-switch UI yet — that's the settings-appearance screen).
if (typeof window !== 'undefined') window.BSTheme = BSTheme;

export default BSTheme;
export { THEME_KEY, THEMES, LABELS, DARK, DEFAULT, get, set, apply, isDark };
